import { request } from './api-client';
import type {
  BlogClusterDetail,
  BlogClusterRow,
  BlogLocaleCopy,
  BlogLocaleCopyInput,
  BlogMediaObject,
  BlogMediaState,
  BlogPublishResult,
  BlogRevisionDetail,
  BlogRevisionSummary,
  BlogValidationIssue,
  BlogValidationVerdict,
  FactDefinition,
} from '@/lib/blog/types';

/**
 * Thin wrappers over `/v1/admin/blog/*`, in the estate's service style.
 *
 * The shared client already resolves `${API_BASE_URL}/v1/admin`, injects the
 * bearer, and — importantly for the upload — leaves a `FormData` body's
 * `Content-Type` alone so the browser can set the multipart boundary. That was
 * checked before reusing it rather than forking auth handling.
 */

export interface BlogClusterListParams {
  page?: number;
  pageSize?: number;
}

export async function listBlogClusters(
  params: BlogClusterListParams = {},
): Promise<{ data: BlogClusterRow[]; meta: { total: number } }> {
  const query = new URLSearchParams();
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
  const suffix = query.toString() === '' ? '' : `?${query.toString()}`;
  return request(`/blog/posts${suffix}`);
}

export async function getBlogCluster(slug: string): Promise<{ data: BlogClusterDetail }> {
  return request(`/blog/posts/${slug}`);
}

export async function getBlogLocaleHead(
  slug: string,
  locale: string,
): Promise<{ data: BlogLocaleCopy }> {
  return request(`/blog/posts/${slug}/locales/${locale}`);
}

export async function saveBlogLocaleHead(
  slug: string,
  locale: string,
  input: BlogLocaleCopyInput,
): Promise<{ data: { version: number } }> {
  return request(`/blog/posts/${slug}/locales/${locale}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function listBlogRevisions(
  slug: string,
  locale: string,
): Promise<{ data: BlogRevisionSummary[] }> {
  return request(`/blog/posts/${slug}/locales/${locale}/revisions`);
}

export async function getBlogRevision(
  slug: string,
  locale: string,
  version: number,
): Promise<{ data: BlogRevisionDetail }> {
  return request(`/blog/posts/${slug}/locales/${locale}/revisions/${String(version)}`);
}

/**
 * A restore is a save, so it carries `baseVersion` and can lose the same race.
 *
 * Reconciliation #5: the stale guard applies to restores too — otherwise
 * restoring an old revision could silently overwrite an edit made while the
 * history panel was open.
 */
export async function restoreBlogRevision(
  slug: string,
  locale: string,
  version: number,
  input: { baseVersion: number },
): Promise<{ data: { version: number } }> {
  return request(`/blog/posts/${slug}/locales/${locale}/revisions/${String(version)}/restore`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * The cluster's fact **definitions** — identity and provenance, never wording.
 *
 * A fact's rendering travels with the locale copy that renders it (AM-18), so
 * this route can no longer overwrite every other language with one editor's
 * English.
 */
export async function saveBlogClusterFacts(
  slug: string,
  input: { clusterVersion: number; facts: FactDefinition[] },
): Promise<{ data: { clusterVersion: number } }> {
  return request(`/blog/posts/${slug}/facts`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/**
 * `step.minutes`, which is cluster-level for the same reason facts are (Q15).
 *
 * Server-side read-modify-write with no client `baseVersion` (reconciliation
 * #2): the value is a per-step scalar, so last-write-wins on one number is
 * honest, where a whole-cluster precondition would reject unrelated edits.
 */
export async function saveBlogClusterStepMinutes(
  slug: string,
  input: { steps: { index: number; minutes: number }[] },
): Promise<{ data: { clusterVersion: number } }> {
  return request(`/blog/posts/${slug}/step-minutes`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function getBlogValidation(slug: string): Promise<{ data: BlogValidationVerdict }> {
  return request(`/blog/posts/${slug}/validation`);
}

export async function publishBlogCluster(slug: string): Promise<{ data: BlogPublishResult }> {
  return request(`/blog/posts/${slug}/publish`, { method: 'POST' });
}

export async function getBlogMedia(slug: string): Promise<{ data: BlogMediaState }> {
  return request(`/blog/posts/${slug}/media`);
}

export interface BlogMediaUploadInput {
  locale: string;
  screenshotId: string;
  theme: 'light' | 'dark';
  viewport: 'desktop' | 'mobile';
  file: File;
  /** Q19 — required, short, and stored with the row. */
  reason: string;
  /** Q27 — set only when the operator has seen the shape warning and meant it. */
  acceptDimensions?: boolean;
}

export async function uploadBlogMediaVariant(
  slug: string,
  input: BlogMediaUploadInput,
): Promise<{ data: BlogMediaObject }> {
  const body = new FormData();
  body.set('file', input.file);
  body.set('screenshotId', input.screenshotId);
  body.set('theme', input.theme);
  body.set('viewport', input.viewport);
  body.set('note', input.reason);
  if (input.acceptDimensions === true) {
    body.set('acceptDimensions', 'true');
  }
  return request(`/blog/posts/${slug}/locales/${input.locale}/media`, {
    method: 'POST',
    body,
  });
}

// ============================================================================
// Failure classification
// ============================================================================

/**
 * A 409 from a write path, sorted into the three things it can mean.
 *
 * The API answers all three — a lost race, a locked address and a taken
 * address — with the same status and no machine-readable discriminator, so
 * this is decided structurally rather than by parsing the sentence: refetch the
 * head, and if it has moved past the version we submitted, we lost a race.
 * Anything else is a rule the server enforced, and its own sentence is the
 * honest thing to show.
 */
export type BlogWriteConflict =
  | { kind: 'stale'; message: string; head: BlogLocaleCopy }
  | { kind: 'rejected'; message: string };

export interface HttpFailure {
  status?: number;
  message: string;
  details?: unknown;
}

export function isConflict(error: HttpFailure): boolean {
  return error.status === 409;
}

export async function classifyWriteConflict(args: {
  slug: string;
  locale: string;
  baseVersion: number;
  message: string;
}): Promise<BlogWriteConflict> {
  const { data: head } = await getBlogLocaleHead(args.slug, args.locale);
  if (head.version !== args.baseVersion) {
    return { kind: 'stale', message: args.message, head };
  }
  return { kind: 'rejected', message: args.message };
}

/**
 * The blockers behind a refused publish.
 *
 * The gate answers 400 with the issue list in `details`; anything else is a
 * transport failure and must not be rendered as if the post were invalid.
 */
export function publishBlockers(error: HttpFailure): BlogValidationIssue[] {
  if (error.status !== 400 || !Array.isArray(error.details)) return [];
  return error.details.filter(
    (issue): issue is BlogValidationIssue =>
      typeof issue === 'object' &&
      issue !== null &&
      typeof (issue as BlogValidationIssue).path === 'string' &&
      typeof (issue as BlogValidationIssue).code === 'string' &&
      typeof (issue as BlogValidationIssue).message === 'string',
  );
}
